import { ActionContext, ActionTree } from 'vuex'
import { IAuthState, ILeadAliasResponse, IRootState } from '@/@types'
import { actionErrorHandling } from '@/helpers'
import {
  fetchMFACode,
  fetchStatus,
  fetchTrackLeadAlias,
  loginUser,
  postMFACode,
} from '@/models/auth'
import { removeAllTokens, setAccessToken } from '@internetworkexpert/js-common'

import CONSTANTS from '@/constants'
import { eventsConstants } from '@/helpers/eventsList'
import router from '@/router'
import { clearGuestUserProfile, persistGuestUserProfile } from '@/helpers/guestUserStorage'

const actions: ActionTree<IAuthState, IRootState> = {
  login: async (
    context: ActionContext<IAuthState, IRootState>,
    formData: { email: string; password: string; recaptcha: string }
  ) => {
    try {
      const user = await loginUser(formData)
      if (user?.tokens?.data) {
        const { Bearer } = user.tokens.data
        await setAccessToken(Bearer)
      }
      const { mfa_method } = user
      if (mfa_method && mfa_method.length) {
        context.commit('USER_PROFILE_UPDATED', {
          mfa_method: user.mfa_method,
        })
        context.commit('IS_TWO_FACTOR_REQUIRED_UPDATED', true)
      } else {
        // @ToDo: do we need subscription info here?
        context.commit('USER_PROFILE_UPDATED', {
          first_name: user.profile.data.first_name,
          last_name: user.profile.data.last_name,
          avatar: user.profile.data.avatar,
          preferred_phone_number: user.profile.data.preferred_phone_number,
          email: user.email,
          user_id: user.id,
        })
        await context.commit('IS_AUTHENTICATED_UPDATED', true)
        clearGuestUserProfile()
        await context.dispatch('subscriptions/getSubscriptionsWithPasses', null, { root: true })
        context.dispatch('featureFlags/initializeDarkly', null, { root: true })
      }
    } catch (error) {
      context.commit('USER_PROFILE_UPDATED', {})
      context.commit('IS_AUTHENTICATED_UPDATED', false)
      context.dispatch('featureFlags/switchDarklyContextToAnonymous')
      throw error
    }
  },
  attemptLogin: async (context: ActionContext<IAuthState, IRootState>, token: string) => {
    if (token) {
      try {
        const user = await fetchStatus()
        context.commit('USER_PROFILE_UPDATED', {
          first_name: user.profile.data.first_name,
          last_name: user.profile.data.last_name,
          avatar: user.profile.data.avatar,
          preferred_phone_number: user.profile.data.preferred_phone_number,
          email: user.email,
          user_id: user.id,
        })
        context.commit('IS_AUTHENTICATED_UPDATED', true)
        clearGuestUserProfile()
        await context.dispatch('subscriptions/getSubscriptionsWithPasses', null, { root: true })
        return user
      } catch (error) {
        context.commit('USER_PROFILE_UPDATED', {})
        context.commit('IS_AUTHENTICATED_UPDATED', false)
        context.dispatch('featureFlags/switchDarklyContextToAnonymous')
      }
    }
  },
  logout: async (context: ActionContext<IAuthState, IRootState>) => {
    try {
      await removeAllTokens()
      clearGuestUserProfile()
      context.dispatch('featureFlags/switchDarklyContextToAnonymous')
    } catch (error) {
      actionErrorHandling(context, error)
    }
  },
  createUserAccount: async (context: ActionContext<IAuthState, IRootState>, payload) => {
    const user = payload
    context.commit('USER_PROFILE_UPDATED', user)
    if (user?.password && user?.email && user?.first_name && user?.last_name) {
      const snapshot = {
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        password: user.password,
      }
      if (user?.recaptcha) {
        Object.assign(snapshot, { recaptcha: user.recaptcha })
      }
      persistGuestUserProfile(snapshot)
    }
  },
  createLeadAlias: async (context: ActionContext<IAuthState, IRootState>, email) => {
    try {
      const response: ILeadAliasResponse = await fetchTrackLeadAlias(email)
      context.commit('SET_LEAD_ALIAS', response.response.id)
      return response.response.id
    } catch (error) {
      return null
    }
  },
  saveRedirectUrl: (context: ActionContext<IAuthState, IRootState>, redirectUrl: string) => {
    window.localStorage.setItem('redirectUrl', redirectUrl)
  },

  submitMFACode: async (context: ActionContext<IAuthState, IRootState>, code: string) => {
    try {
      const user = await postMFACode(code)
      context.commit('IS_TWO_FACTOR_REQUIRED_UPDATED', false)
      context.commit('USER_PROFILE_UPDATED', {
        first_name: user.profile.data.first_name,
        last_name: user.profile.data.last_name,
        avatar: user.profile.data.avatar,
        email: user.email,
        user_id: user.id,
      })
      await context.commit('IS_AUTHENTICATED_UPDATED', true)
      clearGuestUserProfile()
      await context.dispatch('subscriptions/getSubscriptionsWithPasses', null, { root: true })
      context.dispatch('featureFlags/initializeDarkly', null, { root: true })
    } catch (error) {
      actionErrorHandling(context, error)
      throw error
    }
  },

  resendMFACode: async (context: ActionContext<IAuthState, IRootState>) => {
    try {
      await fetchMFACode()
    } catch (error) {
      actionErrorHandling(context, error)
    }
  },
  handleLoginSuccess: async (
    context: ActionContext<IAuthState, IRootState>,
    redirectUrl: string
  ) => {
    context.dispatch('app/setIsComponentLoading', true, { root: true })
    await context.dispatch('subscriptions/getCurrentPaymentMethodOnFile', '', { root: true })
    await context.dispatch('products/fetchAvailableProducts', '', { root: true })
    await context.dispatch('products/getPassTypes', '', { root: true })
    await context.dispatch('subscriptions/getSubscriptionsWithPasses', null, { root: true })
    await context.dispatch('business/getBusinessUser', null, { root: true })
    const GAObj = {
      event: eventsConstants.CHECKOUT_GA_LOGIN,
      user_id: context.rootState.auth.userProfile.user_id || context.rootState.auth.userProfile.id,
    }
    await context.dispatch('events/sendGAEvent', GAObj, { root: true })
    await context.dispatch(
      'events/sendMixpanelEvent',
      {
        event_name: eventsConstants.ACCOUNT_USER_LOGIN,
        properties: { value: GAObj.user_id },
      },
      { root: true }
    )
    context.dispatch('shoppingCart/filterAndUpdateShoppingCartWithExistingSubs', null, {
      root: true,
    })

    const logInRedirectValidation = () => {
      const userHasSubscription =
        context.rootState.subscriptions.usersCurrentSubscriptions.length > 0
      const codes = Object.values(CONSTANTS.PLANS)
      const userHasSubscriptionInCart = context.rootState.shoppingCart.shoppingCart.filter(item =>
        codes.includes(item.merchant_id!)
      )
      return userHasSubscription && userHasSubscriptionInCart ? '/' : 'payment'
    }
    if (router.currentRoute.value.name === 'account') redirectUrl = logInRedirectValidation()

    switch (redirectUrl) {
      case 'payment':
        context.dispatch('app/setIsComponentLoading', true, { root: true })
        context.dispatch('app/setIsSignInModalShown', false, { root: true })
        router.push({ name: 'payment' })
        break
      default:
        context.dispatch('app/setIsComponentLoading', true, { root: true })
        router.go(0)
        if (logInRedirectValidation() === '/' && router.currentRoute.value.name !== 'shop')
          context.dispatch(
            'app/setError',
            'You already have a subscription. Add the standalone voucher instead.',
            {
              root: true,
            }
          )
        break
    }
  },
}

export default actions
